import React, { Suspense, useState, useEffect } from "react";
import { HashRouter, Switch } from "react-router-dom";
import { useAuth0 } from "./auth0/react-auth0-wrapper";
import Authentication from "./views/authentication/Authentication";
import UserRoleContext from "./context/userrolecontext";
import PrivateRoute from "./auth0/PrivateRoute";
import Machines from "./views/machines/Machines";
import Machine from "./views/machine/Machine";
import NotFound from "./views/notfound/NotFound";
import PasswordChange from "./views/passwordchange/Passwordchange";
import LanguageContext from "./context/languagecontext";

const apiUri = process.env.REACT_APP_API_URI;

const Organisations = React.lazy(() =>
  import("./views/usermanagement/Organisations")
);
const AddOrganisation = React.lazy(() =>
  import("./views/usermanagement/Addorganisation")
);
const EditOrganisation = React.lazy(() =>
  import("./views/usermanagement/Editorganisation")
);
const Machinegroups = React.lazy(() =>
  import("./views/usermanagement/Machinegroups")
);
const AddMachinegroup = React.lazy(() =>
  import("./views/usermanagement/Addmachinegroup")
);
const EditMachinegroup = React.lazy(() =>
  import("./views/usermanagement/Editmachinegroup")
);

const App = () => {
  const [userRole, setUserRole] = useState({ role: "user" });
  const [language, setLanguage] = useState("de");
  const languageValue = { language, setLanguage };
  const { loading, getTokenSilently, isAuthenticated, logout } = useAuth0();

  // ██╗  ██╗ █████╗  ██████╗██╗  ██╗    ████████╗ ██████╗     ██╗      ██████╗  ██████╗  ██████╗ ██╗   ██╗████████╗    ██╗   ██╗███████╗███████╗██████╗ ███████╗
  // ██║  ██║██╔══██╗██╔════╝██║ ██╔╝    ╚══██╔══╝██╔═══██╗    ██║     ██╔═══██╗██╔════╝ ██╔═══██╗██║   ██║╚══██╔══╝    ██║   ██║██╔════╝██╔════╝██╔══██╗██╔════╝
  // ███████║███████║██║     █████╔╝        ██║   ██║   ██║    ██║     ██║   ██║██║  ███╗██║   ██║██║   ██║   ██║       ██║   ██║███████╗█████╗  ██████╔╝███████╗
  // ██╔══██║██╔══██║██║     ██╔═██╗        ██║   ██║   ██║    ██║     ██║   ██║██║   ██║██║   ██║██║   ██║   ██║       ██║   ██║╚════██║██╔══╝  ██╔══██╗╚════██║
  // ██║  ██║██║  ██║╚██████╗██║  ██╗       ██║   ╚██████╔╝    ███████╗╚██████╔╝╚██████╔╝╚██████╔╝╚██████╔╝   ██║       ╚██████╔╝███████║███████╗██║  ██║███████║
  // ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝       ╚═╝    ╚═════╝     ╚══════╝ ╚═════╝  ╚═════╝  ╚═════╝  ╚═════╝    ╚═╝        ╚═════╝ ╚══════╝╚══════╝╚═╝  ╚═╝╚══════╝
  //
  // (when browser tab or window was closed without logout and someone opens a new window and enters the uri of the app)
  window.history.length === 2 && isAuthenticated && logout();
  // Remove the line above when changing to websocket

  useEffect(() => {
    let isCanceled = false;
    const controller = new AbortController();

    if (!loading && isAuthenticated) {
      const fetchData = async () => {
        try {
          const token = await getTokenSilently();
          const response = await fetch(`${apiUri}/auth0/userrole`, {
            headers: {
              Authorization: `Bearer ${token}`
            },
            signal: controller.signal
          });
          if (isCanceled) {
            return;
          }
          if (!response.ok) {
            throw Error(`${response.status}`);
          }
          const data = await response.json();
          if (!isCanceled) {
            setUserRole(data);
            setLanguage(data.language);
          }
        } catch (err) {
          !isCanceled && console.log(err);
          return;
        }
      };
      fetchData();
    }

    return () => {
      isCanceled = true;
      controller.abort();
    };
  }, [loading, isAuthenticated, getTokenSilently]);

  if (loading) {
    return (
      <HashRouter>
        <Authentication />
      </HashRouter>
    );
  }

  return (
    <UserRoleContext.Provider value={userRole}>
      <LanguageContext.Provider value={languageValue}>
        <HashRouter>
          <Suspense
            fallback={
              <HashRouter>
                <Authentication />
              </HashRouter>
            }
          >
            <Switch>
              <PrivateRoute exact path="/" component={Machines} />

              <PrivateRoute
                exact
                path="/machine/:machinename"
                render={props => <Machine {...props} />}
              />

              <PrivateRoute
                exact
                path="/passwordchange"
                component={PasswordChange}
              />

              <PrivateRoute
                exact
                path="/organisations"
                component={Organisations}
              />

              <PrivateRoute
                exact
                path="/organisations/add"
                component={AddOrganisation}
              />

              <PrivateRoute
                exact
                path="/organisations/:_id"
                component={EditOrganisation}
              />

              <PrivateRoute
                exact
                path="/organisations/:_id/machinegroups"
                component={Machinegroups}
              />

              <PrivateRoute
                exact
                path="/organisations/:_id/machinegroups/add"
                component={AddMachinegroup}
              />

              <PrivateRoute
                exact
                path="/organisations/:_organisationid/machinegroups/:_id"
                component={EditMachinegroup}
              />

              <PrivateRoute component={NotFound} />
            </Switch>
          </Suspense>
        </HashRouter>
      </LanguageContext.Provider>
    </UserRoleContext.Provider>
  );
};

export default App;
